import React, { useEffect, useMemo, useState } from 'react';
import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    setDoc,
    where,
} from 'firebase/firestore';
import { db } from "../firebaseConfig";
import '../css/chapter.css';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/navbar';

// Small helper to avoid empty strings in Firestore where we want undefined
const nilIfEmpty = (v) => (typeof v === 'string' ? v.trim() || '' : v);

const blankEvent = () => ({
    title: '',
    description: '',
    date: '', // ISO date string (YYYY-MM-DD)
    time: '', // 24h time string (HH:mm)
    location: '',
    talkLink: '',
});

// Event sorting helpers
const MAX_DATE = new Date(8640000000000000); // push undated to end
const toDateObj = (evt) => {
    if (!evt || !evt.date) return MAX_DATE;
    const [y, m, d] = String(evt.date).split('-').map(Number);
    const [hh = 0, mm = 0] = String(evt.time || '00:00').split(':').map(Number);
    return new Date(y || 0, (m || 1) - 1, d || 1, hh || 0, mm || 0);
};
const sortEvents = (arr = []) =>
    [...arr].sort((a, b) => {
        const da = toDateObj(a).getTime();
        const db = toDateObj(b).getTime();
        if (da !== db) return da - db; // ascending by datetime
        // Stable-ish tie-breaker so order is deterministic
        return (a.title || '').localeCompare(b.title || '');
    });

// Helper to compute a friendly label for each school
const schoolLabel = (s) =>
    (s && (s.displayName || s.name || s.schoolName)) ||
    (s && typeof s.id === 'string' ? s.id.replace(/[-_]/g, ' ').toUpperCase() : '');


export default function EditChapter() {
    const navigate = useNavigate();
    const params = useParams();

    // Route can use many possible param names; also support unknown param names by taking the first value.
    const routeKey = useMemo(() => {
        const direct = params.schoolId || params.school || params.slug || params.id || params.schoolSlug || params.chapter || '';
        const first = direct || Object.values(params || {})[0] || '';
        return String(first || '').trim();
    }, [params]);

    const [schools, setSchools] = useState([]);
    const [loadingSchools, setLoadingSchools] = useState(true);

    // This is the Firestore doc id we actually loaded (may differ from routeKey if routeKey is a `slug`).
    const [selectedId, setSelectedId] = useState('');

    const [announcements, setAnnouncements] = useState('');
    const [events, setEvents] = useState([]);

    // Public Chapter fields
    const [displayName, setDisplayName] = useState('');
    const [emailListUrl, setEmailListUrl] = useState('');
    const [groupLink, setGroupLink] = useState(''); // GroupMe invite URL
    const [instagramUrl, setInstagramUrl] = useState('');
    const [websiteUrl, setWebsiteUrl] = useState('');

    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState('');
    const [showToast, setShowToast] = useState(false);
    const [mediaSheetUrl, setMediaSheetUrl] = useState('');

    // Load the list of schools (used for friendly labels + validation)
    useEffect(() => {
        let canceled = false;
        async function run() {
            try {
                setLoadingSchools(true);
                const snap = await getDocs(collection(db, 'schools'));
                if (canceled) return;
                const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
                rows.sort((a, b) => schoolLabel(a).localeCompare(schoolLabel(b)));
                setSchools(rows);
            } catch (err) {
                console.error('Failed to load schools', err);
                setStatus('Error loading schools. See console for details.');
            } finally {
                setLoadingSchools(false);
            }
        }
        run();
        return () => {
            canceled = true;
        };
    }, []);

    // Resolve the school from the URL. The URL value can be either the doc id (e.g. "tamu")
    // or a "slug" field stored on the school document.
    useEffect(() => {
        let canceled = false;

        async function run() {
            if (!routeKey) {
                setSelectedId('');
                return;
            }

            try {
                setStatus('');

                // 1) Try direct doc id lookup first
                const directRef = doc(db, 'schools', routeKey);
                const directSnap = await getDoc(directRef);
                if (canceled) return;

                if (directSnap.exists()) {
                    setSelectedId(routeKey);
                    return;
                }

                // 2) Fall back to slug lookup
                const q = query(collection(db, 'schools'), where('slug', '==', routeKey));
                const qsnap = await getDocs(q);
                if (canceled) return;

                if (!qsnap.empty) {
                    setSelectedId(qsnap.docs[0].id);
                    return;
                }

                setSelectedId('');
                setStatus(`School not found: ${routeKey}`);
            } catch (err) {
                console.error('Failed to resolve school from URL', err);
                setSelectedId('');
                setStatus('Error resolving school from URL. See console for details.');
            }
        }

        run();
        return () => {
            canceled = true;
        };
    }, [routeKey]);

    // Load a single school doc when chosen
    useEffect(() => {
        if (!selectedId) {
            // Clear fields so we don't show stale data.
            setAnnouncements('');
            setEvents([]);
            setDisplayName('');
            setEmailListUrl('');
            setGroupLink('');
            setInstagramUrl('');
            setWebsiteUrl('');
            setMediaSheetUrl('');
            return;
        }
        let canceled = false;
        async function run() {
            try {
                setStatus('');
                const ref = doc(db, 'schools', selectedId);
                const snap = await getDoc(ref);
                if (!snap.exists()) {
                    setStatus(`School not found: ${selectedId}`);
                    // Clear fields so we don't show stale data.
                    setAnnouncements('');
                    setEvents([]);
                    setDisplayName('');
                    setEmailListUrl('');
                    setGroupLink('');
                    setInstagramUrl('');
                    setWebsiteUrl('');
                    setMediaSheetUrl('');
                    return;
                }
                const data = snap.data() || {};
                setAnnouncements(data.announcements || data.announcement || '');
                setEvents(Array.isArray(data.events) ? sortEvents(data.events) : []);

                // Public fields
                setDisplayName(data.displayName || data.name || data.schoolName || '');
                setEmailListUrl(data.ctaEmail || data.emailListUrl || '');
                setGroupLink(data.ctaGroupMe || data.groupMeUrl || '');
                setInstagramUrl(data.ctaInstagram || data.instagramUrl || '');
                setWebsiteUrl(data.ctaWebsite || data.websiteUrl || '');
                setMediaSheetUrl(data.mediaSheetUrl || '');
            } catch (err) {
                console.error('Failed to load school', err);
                setStatus('Error loading selected school. See console for details.');
            }
        }
        run();
        return () => {
            canceled = true;
        };
    }, [selectedId]);

    const selectedSchoolName = useMemo(() => {
        if (!selectedId) return routeKey || '';
        const row = schools.find((s) => s.id === selectedId);
        return row ? (row.displayName || row.name || row.schoolName || row.id) : selectedId;
    }, [schools, selectedId, routeKey]);


    function onChangeEvent(idx, field, value) {
        setEvents((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], [field]: value };
            return next;
        });
    }

    function addEvent() {
        setEvents((prev) => [...prev, blankEvent()]);
    }

    function removeEvent(idx) {
        setEvents((prev) => prev.filter((_, i) => i !== idx));
    }


    async function saveAll() {
        if (!selectedId) {
            setStatus('Please select a school to save.');
            return;
        }
        setSaving(true);
        setStatus('');
        try {
            const ref = doc(db, 'schools', selectedId);
            // Ensure document exists; merge if it already does
            await setDoc(
                ref,
                {
                    announcements: announcements || '',

                    // Public Chapter data used by the public page
                    displayName: nilIfEmpty(displayName) || '',
                    ctaEmail: nilIfEmpty(emailListUrl) || '',
                    ctaGroupMe: nilIfEmpty(groupLink) || '',
                    ctaInstagram: nilIfEmpty(instagramUrl) || '',
                    ctaWebsite: nilIfEmpty(websiteUrl) || '',
                    mediaSheetUrl: nilIfEmpty(mediaSheetUrl) || '',
                    events: sortEvents(events || []).map((e) => ({
                        title: nilIfEmpty(e.title),
                        description: nilIfEmpty(e.description),
                        date: e.date || '',
                        time: e.time || '',
                        location: nilIfEmpty(e.location),
                        talkLink: nilIfEmpty(e.talkLink),
                    })),
                    updatedAt: new Date().toISOString(),
                },
                { merge: true }
            );
            // After save, update local state to sorted order
            setEvents((prev) => sortEvents(prev));
            setStatus('Saved!');
            setShowToast(true);
            setTimeout(() => {
                setShowToast(false);
            }, 2000);
        } catch (err) {
            console.error('Failed to save', err);
            setStatus('Error saving. See console for details.');
            setShowToast(true);
        } finally {
            setSaving(false);
        }
    }

    // Save on Ctrl+S or Cmd+S
    useEffect(() => {
        function handleKeyDown(e) {
            if ((e.metaKey || e.ctrlKey) && e.key === 's') {
                e.preventDefault();
                if (!saving && selectedId) {
                    saveAll();
                }
            }
        }
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [saving, selectedId, announcements, events, displayName, emailListUrl, groupLink, instagramUrl, websiteUrl, mediaSheetUrl]);

    return (
        <div className="chapter-editor">
            <Navbar />
            <div className='chapter-content'>

                {/* Local nav / header */}
                <div className="chapter-box" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h1 style={{ margin: 0 }}>Chapter Editor</h1>
                        <p style={{ margin: 0, color: '#6c757d' }}>Edit announcements, public page settings, and this semester's events.</p>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Link to="/national"><button className="secondary">← Back to Dashboard</button></Link>
                        {selectedId ? (
                            <Link to={`/schools/${selectedId}`} target="_blank" rel="noopener noreferrer">
                                <button>View Chapter Page</button>
                            </Link>
                        ) : (
                            <button disabled>View Chapter Page</button>
                        )}
                    </div>
                </div>

                {/* School (from URL) */}
                <div className="chapter-box">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <div>
                            <label>School</label>
                            <div style={{ marginTop: 6 }}>
                                {loadingSchools ? (
                                    <span style={{ color: '#6c757d' }}>Loading…</span>
                                ) : (
                                    <strong>{selectedSchoolName || selectedId}</strong>
                                )}
                                <div style={{ color: '#6c757d', marginTop: 2, fontSize: 12 }}>
                                    Using URL: <code>/edit-chapter/{routeKey || '…'}</code>
                                    {routeKey && selectedId && routeKey !== selectedId ? (
                                        <span style={{ marginLeft: 8 }}>
                                            (resolved to <code>{selectedId}</code>)
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                        <Link to="/chapters">
                            <button className="secondary">Change school</button>
                        </Link>
                    </div>
                </div>

                {/* Announcements */}
                <div className="chapter-box">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <label htmlFor="announcements">Current Announcements</label>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            <small>
                                Saved to <code>schools/{selectedId || '—'}</code> as <code>announcements</code>
                            </small>
                        </div>
                    </div>
                    <textarea
                        id="announcements"
                        rows={5}
                        placeholder="Share any current notes for your chapter (e.g., room changes, campus notes, sign-up links)."
                        value={announcements}
                        onChange={(e) => setAnnouncements(e.target.value)}
                        disabled={!selectedId}
                        className="full"
                    />
                </div>

                {/* Public Page Settings */}
                <div className="chapter-box">
                    <h2 style={{ marginTop: 0 }}>Public Page Settings</h2>

                    <label htmlFor="display-name">University Name</label>
                    <input
                        id="display-name"
                        type="text"
                        placeholder="Texas A&amp;M University"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        disabled={!selectedId}
                    />
                </div>

                {/* Get Involved Links */}
                <div className="chapter-box">
                    <h2 style={{ marginTop: 0 }}>Get Involved Links</h2>

                    <label htmlFor="group-link">Group Chat Link</label>
                    <input
                        id="group-link"
                        type="url"
                        placeholder="https://groupme.com/join/…"
                        value={groupLink}
                        onChange={(e) => setGroupLink(e.target.value)}
                        disabled={!selectedId}
                    />
                    <small>Shown as <em>Join Our GroupMe</em>.</small>

                    <label htmlFor="email-link" style={{ marginTop: 12, display: 'block' }}>Email List Link</label>
                    <input
                        id="email-link"
                        type="url"
                        placeholder="https://… (Mailchimp, Google Form, etc.)"
                        value={emailListUrl}
                        onChange={(e) => setEmailListUrl(e.target.value)}
                        disabled={!selectedId}
                    />
                    <small>Shown as <em>Join Our Email List</em>.</small>

                    <label htmlFor="instagram-link" style={{ marginTop: 12, display: 'block' }}>Instagram URL</label>
                    <input
                        id="instagram-link"
                        type="url"
                        placeholder="https://instagram.com/…"
                        value={instagramUrl}
                        onChange={(e) => setInstagramUrl(e.target.value)}
                        disabled={!selectedId}
                    />

                    <label htmlFor="website-link" style={{ marginTop: 12, display: 'block' }}>Chapter Website</label>
                    <input
                        id="website-link"
                        type="url"
                        placeholder="https://…"
                        value={websiteUrl}
                        onChange={(e) => setWebsiteUrl(e.target.value)}
                        disabled={!selectedId}
                    />
                </div>

                {/* Events Editor */}
                <div className="chapter-box">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2 style={{ margin: 0 }}>Semester Events</h2>
                        <button onClick={addEvent} disabled={!selectedId}>+ Add Event</button>
                    </div>

                    {!events?.length && (
                        <p>No events yet. Click “Add Event” to create your first one.</p>
                    )}

                    <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
                        {events.map((e, idx) => (
                            <div key={idx} className="event-card">
                                <div className="event-grid">
                                    <div>
                                        <label>Title</label>
                                        <input
                                            type="text"
                                            value={e.title}
                                            onChange={(ev) => onChangeEvent(idx, 'title', ev.target.value)}
                                            placeholder="e.g., Intro Night: Why SOLE?"
                                        />
                                    </div>
                                    <div>
                                        <label>Location</label>
                                        <input
                                            type="text"
                                            value={e.location}
                                            onChange={(ev) => onChangeEvent(idx, 'location', ev.target.value)}
                                            placeholder="e.g., MSC 2406"
                                        />
                                    </div>
                                    <div>
                                        <label>Date</label>
                                        <input
                                            type="date"
                                            value={e.date}
                                            onChange={(ev) => onChangeEvent(idx, 'date', ev.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label>Time</label>
                                        <input
                                            type="time"
                                            value={e.time}
                                            onChange={(ev) => onChangeEvent(idx, 'time', ev.target.value)}
                                        />
                                    </div>
                                    <div className="event-description">
                                        <label>Description</label>
                                        <textarea
                                            rows={3}
                                            value={e.description}
                                            onChange={(ev) => onChangeEvent(idx, 'description', ev.target.value)}
                                            placeholder="What is this event about?"
                                        />
                                    </div>
                                    <div className="event-url">
                                        <label>Talk Link</label>
                                        <input
                                            type="url"
                                            value={e.talkLink}
                                            onChange={(ev) => onChangeEvent(idx, 'talkLink', ev.target.value)}
                                            placeholder="https://… (optional)"
                                        />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                                    <button className="secondary" onClick={() => removeEvent(idx)}>Remove</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button onClick={saveAll} disabled={!selectedId || saving}>
                        {saving ? 'Saving…' : 'Save Changes'}
                    </button>
                    <span className={`status-text ${status === 'Saved!' ? 'success' : status ? 'error' : ''}`}>{status}</span>
                </div>
                {/* Save Toast (styles added in CSS) */}
                <div
                    className={`save-toast ${showToast ? 'show' : ''} ${status && status.startsWith('Error') ? 'error' : ''}`}
                    role="status"
                    aria-live="polite"
                >
                    <span className="dot" />
                    <span>{status || (saving ? 'Saving…' : '')}</span>
                </div>
            </div>
        </div>
    );
}